
    import { defineComponent, PropType, computed, reactive, ref, watch } from 'vue'
    import router from '@/router'
    import { useEquipmentStore } from '@/store/equipment'
    import { useUserStore } from '@/store/user'
    import { useUserStateStore } from '@/store/userState'

    import {
        TreeList,
        filterBy,
        orderBy,
        TreeListColumnProps,
        mapTree,
        TreeListExpandChangeEvent,
        TreeListDataStateChangeEvent
    } from '@progress/kendo-vue-treelist'
    import { Checkbox } from '@progress/kendo-vue-inputs'
    import { Label } from '@progress/kendo-vue-labels'
    import { Splitter, SplitterOnChangeEvent, SplitterPaneProps } from '@progress/kendo-vue-layout'
    import Indicator from '@/components/common/my-indicator.vue'
    import MyIcon from '@/components/common/my-icon.vue'
    import StockIndicator from '@/components/common/stock-indicator.vue'
    import MaterialExplodeBar from './material-explode-bar.vue'
    import SearchResultPanel from './search-result-panel.vue'
    import PurchaseButton from '@/components/common/purchase-button.vue'
    import MaterialDetails from './material-details.vue'
    import CloseButtonHeader from '@/components/common/close-button-header.vue'
    import SearchMatchViewer from './search-match-viewer.vue'

    import { FilterDescriptor, SortDescriptor } from '@progress/kendo-data-query'
    import { MaterialListItem, buildPriceFormatter, MaterialTreeItem } from '@/types/material'
    import '@/types/ArrayExtensions'
    import { ISearchInput, ISearchMatch } from '@/types/search'
    import { BomTreeNodeType, IBomHeaderDTO, IBomItemQtyDTO } from '@/types/bom'
    import { IMaterialTreeListState } from '@/types/userState'

    import { BomTreeNodesToMaterialItemListTransformer, NodeTypeUtil } from '@/lib/bom'
    import { buildLocalization, buildClassificationLocalization, ItemScroller, NumDictionary, StringUtil } from '@/util'    

    export default defineComponent({
        name: 'MaterialTreeList',

        components: {
            SearchMatchViewer,
            'k-label': Label,
            CloseButtonHeader,
            Indicator,
            Checkbox,
            TreeList,
            MaterialDetails,
            Splitter,
            StockIndicator,
            MyIcon,
            MaterialExplodeBar,
            SearchResultPanel,
            PurchaseButton
        },

        /********************************************************************************************************************
         PUBLIC PROPERTIES
         ********************************************************************************************************************/
        props: {
            // Optional search pattern to trigger results in the search panel
            searchInput: { type: Object as PropType<ISearchInput>, required: true },

            // Data to display in the tree list
            materialData: { type: Object as PropType<IBomHeaderDTO[]>, required: false },

            // Optional: Different display behavior in "admin menu"
            adminMode: { type: Boolean as PropType<boolean>, required: false, default: () => false },

            // Show "Qty" column in equipment mode
            equipmentMode: { type: Boolean as PropType<boolean>, required: false, default: () => true }
        },

        setup(props) {
            /********************************************************************************************************************
             PRIVATE DATA
             ********************************************************************************************************************/
            const userStore = useUserStore()
            const userStateStore = useUserStateStore()
            const equipmentStore = useEquipmentStore()
            const itemScroller = new ItemScroller

            const initialState: IMaterialTreeListState = userStateStore.sparePartPageState.treeListState ?? 
                {
                    filterEnabled: false,
                    subItemsField: 'visibleChildItems',
                    expandField: 'expanded',
                    expanded: [],
                    filter: [] as FilterDescriptor[],
                    sort: [] as SortDescriptor[],
                    //    [{
                    //        field: 'description',
                    //        dir: 'asc'
                    //    }]
                    detailsItem: null
            }

            if (initialState.detailsItem) {
                scrollToItem(initialState.detailsItem, false)
                userStateStore.setSparePartTreeState(null)
            }

            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/
            const state = reactive<IMaterialTreeListState>(initialState)

            const splitterPanesState = ref([
                { size: '200px', content: 'searchPanel', collapsible: false, collapsed: true },
                { content: 'treeList' }
            ] as SplitterPaneProps[])

            const activeSearchResult = ref<ISearchMatch | null>(null)

            const publicData = { state, splitterPanesState, activeSearchResult }

            /********************************************************************************************************************
             COMPUTED PROPERTIES
             ********************************************************************************************************************/
            const { currentLanguage, localize } = buildLocalization()
            const { classificationHasLoaded } = buildClassificationLocalization()
            const { priceFormatter } = buildPriceFormatter()

            const splitterPanes = computed(() => splitterPanesState.value
                .filter(section => !section.collapsed)
            )

            const loginType = computed(() => userStore.loginType)
            const hasLoaded = computed(() => (props.materialData?.length ?? 0) > 0 && classificationHasLoaded.value)

            const transformedData = computed(() => hasLoaded.value
                ? getTransformedData()
                : { items: [], lookup: {} }
            )

            const listData = computed(() => transformedData.value.items)
            const nodeLookup = computed(() => transformedData.value.lookup)

            const recursionLevel = computed(() => props.adminMode ? 99 : 1)

            const processedData = computed(() => {
                const filteredData = filterBy(listData.value, state.filter, state.subItemsField)
                const sortedData = orderBy(filteredData, state.sort, state.subItemsField)
                const result = addExpandField(sortedData)
                return result
            })

            const isDetailsViewOpen = computed(() => state.detailsItem != null)

            const isPartnerLogin = computed(() => userStore.userData != null)
            const isAdminLogin = computed(() => userStore.isAdmin)

            const columnDef = computed(() => {
                let columns =
                    [
                        {
                            field: 'description',
                            title: localize('Description'),
                            expandable: true,
                            className: 'firstCol',
                            cell: 'renderDescription'
                        },
                        {
                            field: 'id',
                            title: ' ',
                            cell: 'renderPurchaseButton',
                            sortable: false,
                            filterable: false,
                            visible: loginType.value == 'user' && !props.adminMode,
                            width: 170
                        },
                        {
                            field: 'id',
                            title: ' ',
                            cell: 'renderStatus',
                            sortable: false,
                            filterable: false,
                            width: 150
                        },
                        {
                            field: 'materialNo',
                            title: localize('MaterialNo'),
                            width: 120,
                            cell: 'renderMaterialNo'
                        },
                        {
                            field: 'qty',
                            title: localize('qty'),
                            width: 130,
                            filter: 'numeric',
                            cell: 'renderRightJustified',
                            visible: props.equipmentMode
                        },
                        {
                            field: 'price',
                            title: localize('Price'),
                            cell: 'renderPrice',
                            width: 130,
                            filterCell: null,
                            visible: isPartnerLogin.value
                        },
                        {
                            field: 'sparePartPackage',
                            title: localize('SparePartPackage'),
                            width: 175
                        },
                        {
                            field: 'availableStock',
                            title: localize('Inventory'),
                            cell: 'renderRightJustified',
                            width: 150,
                            visible: isPartnerLogin.value && isAdminLogin.value && props.adminMode
                        },
                        {
                            field: 'statusText',
                            title: localize('Status'),
                            width: 175,
                            visible: isPartnerLogin.value && isAdminLogin.value && props.adminMode
                        },
                        {
                            field: 'isBulk',
                            title: localize('IsBulk'),
                            filter: 'boolean',
                            width: 100,
                            visible: isPartnerLogin.value && isAdminLogin.value && props.adminMode
                        }

                    ] as (TreeListColumnProps & { visible?: boolean })[]

                return columns.filter(c => c.visible !== false)
            })

            const computedData = {
                priceFormatter,
                currentLanguage,
                splitterPanes,
                loginType,
                hasLoaded,
                listData,
                nodeLookup,
                processedData,
                isDetailsViewOpen,
                isPartnerLogin,
                isAdminLogin,
                columnDef
            }

            /********************************************************************************************************************
             PRIVATE METHODS
             ********************************************************************************************************************/
            function expandNode(id: number) {
                if (!state.expanded.includes(id)) {
                    state.expanded.push(id)
                }
            }

            function collapseNode(id: number) {
                state.expanded = state.expanded.filter(item => item != id)
            }

            function expandAllParentNodes(node: MaterialListItem) {
                let currentNode: MaterialListItem | undefined = node?.parentItem

                while (currentNode) {
                    expandNode(currentNode.id)
                    currentNode = currentNode.parentItem
                }
            }

            //function recSum(data: MaterialListItem[]): number {
            //    var s: number = 0

            //    for (var d of data) {
            //        s += 1
            //        if (d.childItems) {
            //            s += recSum(d.childItems)
            //        }
            //    }

            //    return s
            //}

            function createAppState(dataState: { sort?: SortDescriptor[], filter?: FilterDescriptor[] }) {
                state.sort = dataState.sort ?? []
                state.filter = dataState.filter ?? []
            }

            function addExpandField(dataTree: MaterialListItem[]): MaterialTreeItem[] {
                const expandedState = state.expanded
                const tree = mapTree(dataTree,
                    state.subItemsField,
                    item => new MaterialTreeItem(item, expandedState.includes(item.id))
                )
                return tree
            }

            function getTransformedData() {
                const result = (new BomTreeNodesToMaterialItemListTransformer)
                    .transform(props.materialData ?? [], recursionLevel.value)

                if (props.equipmentMode) {
                    // Add equipment bom item quantities to list items
                    const bomItemQtyDict = equipmentStore.bomItemQuantityDict

                    if (bomItemQtyDict != null) {
                        addBomItemQty(result.items, bomItemQtyDict)
                    }
                }

                return result
            }

            function addBomItemQty(items: MaterialListItem[], bomItemQtyDict: NumDictionary<IBomItemQtyDTO>) {
                for (const item of items) {
                    if (item.materialNo != null) {
                        const bomItemQty = bomItemQtyDict[item.materialNo]
                        item.qty = bomItemQty?.qty
                        item.unit = bomItemQty?.unit
                    }

                    if (item.childItems) {
                        addBomItemQty(item.childItems, bomItemQtyDict)
                    }
                }
            }

            /********************************************************************************************************************
             PUBLIC METHODS AND EVENT HANDLERS
             ********************************************************************************************************************/
            function pointerClass(item: MaterialTreeItem): string[] {
                if (!props.adminMode || item.childItems?.length) {
                    return [ 'pointer'] 
                }

                return []
            }

            function onSplitterChange(event: SplitterOnChangeEvent) {
                splitterPanesState.value = event.newState
            }

            function gotoSearchResult(match: ISearchMatch) {
                activeSearchResult.value = match
                expandAllParentNodes(match.node)
                scrollToItem(match.node, true)
            }

            function getItemOrNearestParent(item: MaterialListItem): HTMLElement | null {
                let currentItem: MaterialListItem | undefined = item

                do {
                    const targetId = `Node${currentItem.id}`
                    const target = document.getElementById(targetId)

                    if (target != null) return target

                    currentItem = currentItem?.parentItem
                } while (currentItem != null)

                return null
            }

            function scrollToItem(item: MaterialListItem, smoothScrolling: boolean) {
                itemScroller.scrollToItem(() => getItemOrNearestParent(item), smoothScrolling)

                // also refresh details view, if open
                if (isDetailsViewOpen?.value) {
                    showDetails(item)
                }
            }

            function closeSearchPanel() {
                splitterPanesState.value[0].collapsed = true
            }

            function showDetails(dataItem: MaterialListItem) {
                state.detailsItem = dataItem
            }

            function closeDetails() {
                state.detailsItem = null
            }

            function redirectToMaterialView(materialItem?: MaterialListItem) {
                if (materialItem?.materialNo && !props.adminMode) {
                    state.detailsItem = materialItem
                    userStateStore.setSparePartTreeState(state)
                    router.push({ name: 'material', params: { matno: materialItem.materialNo }})
                }
            }

            function materialPrice(node: MaterialListItem): string {
                return priceFormatter.value(node).formattedPriceWithUnit()
            }

            function onExpandChange(e: TreeListExpandChangeEvent) {
                const dataItem = e.dataItem as MaterialTreeItem

                if (props.adminMode || dataItem.visibleChildItems?.length) {
                    state.expanded = e.value
                        ? state.expanded.filter(id => id !== e.dataItem.id)
                        : [...state.expanded, e.dataItem.id]
                }
                else {
                    // In non-admin mode, jump to material view, when the description text is clicked
                    redirectToMaterialView(dataItem)
                }
            }

            function handleDataStateChange(e: TreeListDataStateChangeEvent) {
                createAppState(e.data)
            }

            function toggleChildNode(item: MaterialTreeItem, nodeType: BomTreeNodeType) {
                const targetNodes = item.childItems?.filter(n => n.nodeType == nodeType)

                if (targetNodes?.length) {
                    const isExpanded = state.expanded.includes(targetNodes[0].id) && state.expanded.includes(item.id)

                    for (const targetNode of targetNodes) {
                        if (isExpanded) {
                            collapseNode(targetNode.id)
                            collapseNode(item.id)
                        }
                        else {
                            expandNode(item.id)
                            expandNode(targetNode.id)
                        }
                    }
                }
            }

            function getClassesForSpecialNode(nodeType: BomTreeNodeType): string[] {
                return [
                    NodeTypeUtil.nodeType2ClassName(nodeType, 'background'),
                    'expand-node'
                ]
            }

            function onDetailsChanged(material: MaterialListItem) {
                if (material.materialNo) {
                    const jumpToNode = nodeLookup.value[material.materialNo]

                    if (jumpToNode) {
                        state.detailsItem = jumpToNode
                    }
                }
            }

            function getExpansionIconClasses(node: MaterialListItem, expanded: boolean): string[] {
                const classes = ['k-icon']

                if (node.isLeaf) {
                    classes.push('k-i-file')
                    classes.push('nav-icon-leaf')
                }
                else {
                    if (expanded) {
                        // expanded
                        classes.push('nav-icon-expanded')
                        classes.push('k-i-folder-open')
                    }
                    else {
                        // collapsed
                        classes.push('nav-icon-collapsed')

                        if (node.isCategoryNode) {
                            classes.push('k-i-folder')
                        }
                        else if (node.isSpecialNode) {
                            classes.push('k-i-folder-more')
                        }
                        else {
                            if (props.adminMode) {
                                classes.push('k-i-file-add')
                            }
                            else {
                                classes.push('k-i-file')
                                classes.push('nav-icon-leaf')
                            }
                        }
                    }
                }

                return classes
            }

            function hoverOnDetailsLink(): string[] {
                return !props.adminMode
                    ? ['ul-link']
                    : []
            }

            const publicMethods = {
                pointerClass,
                onSplitterChange,
                gotoSearchResult,
                closeSearchPanel,
                showDetails,
                closeDetails,
                localize,
                onExpandChange,
                redirectToMaterialView,
                handleDataStateChange,
                toggleChildNode,
                materialPrice,
                getClassesForSpecialNode,
                onDetailsChanged,
                getExpansionIconClasses,
                hoverOnDetailsLink
            }

            /********************************************************************************************************************
             WATCHERS
             ********************************************************************************************************************/
            // Make search result panel appear, when search text was entered
            watch(
                () => props.searchInput,
                (newValue) => {
                    splitterPanesState.value[0].collapsed = StringUtil.isNullOrEmpty(newValue.searchText)
                }
            )

            /********************************************************************************************************************
             INTERFACE
             ********************************************************************************************************************/
            return {
                // DATA
                ...publicData,
                // COMPUTED
                ...computedData,
                // METHODS AND EVENT HANDLERS
                ...publicMethods
            }
        }
    })

